"use client";

import { useEffect, useRef } from "react";

/**
 * Run `fn` when this page is shown again from the back-forward cache.
 *
 * A page left for another one may be kept whole and brought back by the
 * browser's back button — scripts, state and all, exactly as it stood the
 * moment it was left. What stood then is usually something in progress: a
 * button on 「请稍候…」 because the press that took them away was still
 * holding it (Issue 601), a navigation lock (Issue 569), a menu open on the
 * item that signed them out. None of it will be let go of by the page that
 * was left, because that page is over; this is the one moment to do it.
 *
 * Only a restore: an ordinary `pageshow` is the page's first drawing and has
 * nothing to undo. Today's Chrome rarely restores these pages in production
 * — they are answered with `Cache-Control: no-store` — but that is the
 * browser's choice and not a promise, and Safari and Firefox make their own.
 * `next dev` answers without `no-store`, so there Chrome restores them
 * routinely (Issue 819).
 *
 * The newest `fn` is the one called, so a caller need not keep it stable.
 */
export function useRestored(fn: () => void): void {
  const latest = useRef(fn);
  useEffect(() => { latest.current = fn; });
  useEffect(() => {
    function shown(e: PageTransitionEvent) {
      if (e.persisted) latest.current();
    }
    window.addEventListener("pageshow", shown);
    return () => window.removeEventListener("pageshow", shown);
  }, []);
}
